import { useState } from 'react';
import { Link } from 'react-router';
import { CalendarDays, Gift, ShoppingBag, Sparkles } from 'lucide-react';
import { useTenant } from '../lib/tenant';
import { useSession } from '../lib/session';
import { errorMessage } from '../lib/api';
import { Button, Skeleton } from '../ui/kit';
import { TenantLogo } from '../ui/brand';
import { useToast } from '../ui/toast';

const PERKS = [
  { icon: Sparkles, title: 'Her alışverişte yıldız', text: 'Yıldızların birikir, seviyen yükselir.' },
  { icon: Gift, title: 'Ödüller seni bekler', text: 'Kasada karekodunu okut, ödülünü kullan.' },
  { icon: ShoppingBag, title: 'Sıra beklemeden sipariş', text: 'Uygulamadan ver, hazır olunca al.' },
  { icon: CalendarDays, title: 'Masanı önceden ayırt', text: 'Gün ve saati seç, gerisini bize bırak.' },
];

export function Welcome() {
  const { bootstrap, loading, path } = useTenant();
  const { continueAsGuest } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col">
      <section className="card-emerald relative overflow-hidden rounded-b-[36px] px-6 pb-10 pt-safe">
        <div className="pointer-events-none absolute -right-20 -top-10 size-64 rounded-full bg-gold/15 blur-3xl" aria-hidden />
        <div className="pt-10">
          {loading && !bootstrap ? <Skeleton className="size-14 rounded-2xl opacity-40" /> : <TenantLogo size={56} className="animate-pop" />}
          <p className="mt-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-gold-bright/90">Sadakat programı</p>
          <h1 className="mt-2 animate-rise text-[32px] font-extrabold leading-[1.1] tracking-[-0.03em]">
            {bootstrap?.tenant.name ?? <span className="inline-block h-9 w-48 rounded-lg bg-white/10" />}
          </h1>
          <p className="mt-3 max-w-sm animate-rise text-[15px] leading-relaxed text-white/75 [animation-delay:60ms]">
            Üye ol, ilk alışverişinden itibaren yıldız kazanmaya başla.
          </p>
        </div>
      </section>

      <ul className="grid grid-cols-2 gap-3 px-5 pt-6">
        {PERKS.map((p, i) => (
          <li key={p.title} className="animate-rise rounded-2xl bg-surface p-4 shadow-card ring-1 ring-line/60" style={{ animationDelay: `${100 + i * 50}ms` }}>
            <span className="grid size-10 place-items-center rounded-xl bg-gold-soft text-gold-ink dark:text-gold-bright">
              <p.icon className="size-5" />
            </span>
            <p className="mt-3 text-[14px] font-bold leading-tight">{p.title}</p>
            <p className="mt-1 text-[12.5px] leading-snug text-muted">{p.text}</p>
          </li>
        ))}
      </ul>

      <div className="mt-auto space-y-3 px-5 pb-8 pt-8 pb-safe">
        <Link to={path('giris')} className="block">
          <Button block size="lg">
            Üye ol ya da giriş yap
          </Button>
        </Link>
        {bootstrap?.guestCheckout && (
          <Button
            block
            size="lg"
            variant="secondary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await continueAsGuest();
              } catch (e) {
                toast.show(errorMessage(e), 'danger');
              } finally {
                setBusy(false);
              }
            }}
          >
            Üye olmadan sipariş ver
          </Button>
        )}
        <p className="px-4 text-center text-[12px] leading-relaxed text-muted">
          Devam ederek{' '}
          <Link to={path('kosullar')} className="font-semibold text-ink-2 underline underline-offset-2">
            üyelik koşullarını ve aydınlatma metnini
          </Link>{' '}
          okuyabilirsin.
        </p>
      </div>
    </div>
  );
}
